import { AudioRecorder } from '@hakimo-ui/hakimo/feature-shared';
import { Camera } from '@hakimo-ui/hakimo/types';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { useAtomValue } from 'jotai';
import { useEffect, useRef } from 'react';
import { fullScreenTargetAtom } from '../../state';
import TileZoomable from './TileZoomable';

interface Props {
  camera: Camera;
  onRemove: () => void;
  showRemove?: boolean;
  showZoomControls?: boolean;
  onAspectRatio?: (ratio: number) => void;
  paused?: boolean;
  onZoomChange?: (isZoomed: boolean) => void;
  children: React.ReactNode;
}

export function MultiCamTile(props: Props) {
  const {
    camera,
    onRemove,
    showRemove = true,
    showZoomControls = true,
    onAspectRatio,
    paused = false,
    onZoomChange,
    children,
  } = props;
  const containerRef = useRef<HTMLDivElement>(null);
  const fullScreenTarget = useAtomValue(fullScreenTargetAtom);
  const onAspectRatioRef = useRef(onAspectRatio);
  onAspectRatioRef.current = onAspectRatio;
  const pausedRef = useRef(paused);
  pausedRef.current = paused;
  const hasAudioDevices = (camera.audioDevices || []).length > 0;

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return undefined;
    const report = (video: HTMLVideoElement | null) => {
      if (video && video.videoWidth > 0 && video.videoHeight > 0) {
        onAspectRatioRef.current?.(video.videoWidth / video.videoHeight);
      }
      if (video && pausedRef.current) video.pause();
    };
    const handler = (e: Event) => report(e.target as HTMLVideoElement);
    el.addEventListener('loadedmetadata', handler, true);
    report(el.querySelector('video'));
    return () => el.removeEventListener('loadedmetadata', handler, true);
  }, []);

  useEffect(() => {
    const video = containerRef.current?.querySelector('video');
    if (!video) return;
    if (paused) {
      video.pause();
    } else {
      void video.play().catch(() => undefined);
    }
  }, [paused]);

  return (
    <div
      ref={containerRef}
      className="border-onlight-line-2 dark:border-ondark-line-2 group/tile relative h-full min-h-0 min-w-0 overflow-hidden border"
    >
      <TileZoomable
        showZoomControls={showZoomControls}
        onZoomChange={onZoomChange}
        trailing={
          showRemove ? (
            <button
              type="button"
              title="Remove"
              onClick={onRemove}
              className="rounded p-1 text-white outline-none hover:bg-red-500/80"
            >
              <XMarkIcon className="h-4 w-4" aria-hidden="true" />
            </button>
          ) : undefined
        }
      >
        {children}
      </TileZoomable>
      <div className="from-dark-surface pointer-events-none absolute inset-x-0 top-0 z-[5] h-6 bg-gradient-to-b" />
      <div className="from-dark-surface pointer-events-none absolute inset-x-0 bottom-0 z-[5] h-6 bg-gradient-to-t" />
      {hasAudioDevices && (
        <div className="absolute left-1 top-1 z-10 rounded-md bg-black/40 text-white backdrop-blur-sm [&_button]:p-1 [&_svg]:h-4 [&_svg]:w-4">
          <AudioRecorder
            cameraId={camera.id}
            cameraName={camera.name}
            portalTarget={fullScreenTarget ?? undefined}
          />
        </div>
      )}
      {camera.location?.name && (
        <span
          title={camera.location.name}
          className="pointer-events-none absolute left-1/2 top-1 z-10 max-w-[70%] -translate-x-1/2 truncate text-xs text-[#40C4FF]"
        >
          {camera.location.name}
        </span>
      )}
      <span
        title={camera.name}
        className="pointer-events-none absolute bottom-1 left-1/2 z-10 max-w-[70%] -translate-x-1/2 truncate text-xs text-[#40C4FF]"
      >
        {camera.name}
      </span>
    </div>
  );
}

export default MultiCamTile;
